import React from 'react';
import { Activity } from '../types';
import { StatusBadge } from '../components/StatusBadge';
import { PriorityBadge } from '../components/PriorityBadge';
import { ArrowLeftIcon, PlusIcon, UploadIcon, PencilIcon, PrinterIcon, CalendarIcon, MapPinIcon, FileTextIcon, UsersIcon, ClockIcon, DownloadIcon } from 'lucide-react';
interface ActivityDetailPageProps {
  activityId: string;
  activities: Activity[];
  onBack: () => void;
  onEdit: (id: string) => void;
}
export function ActivityDetailPage({
  activityId,
  activities,
  onBack,
  onEdit
}: ActivityDetailPageProps) {
  const activity = activities.find(a => a.id === activityId);
  if (!activity) {
    return <div className="p-8 text-center" data-id="element-141">
        <p className="text-[#FF3B3B] text-sm" data-id="element-142">Kegiatan tidak ditemukan.</p>
        <button onClick={onBack} className="mt-4 text-[#00D4FF] hover:underline text-sm font-medium" data-id="element-143">
          Kembali ke daftar
        </button>
      </div>;
  }
  return <div className="space-y-6 max-w-6xl mx-auto pb-12" data-id="element-144">
      {/* Back Button */}
      <button onClick={onBack} className="flex items-center text-[#94A3B8] hover:text-[#00D4FF] transition-colors text-sm" data-id="element-145">
        <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-146" />
        Kembali ke Daftar Kegiatan
      </button>

      {/* Header Section */}
      <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] p-6 relative overflow-hidden" data-id="element-147">
        <div className="absolute inset-0 cyber-grid opacity-5 pointer-events-none" data-id="element-148"></div>
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-6 relative z-10" data-id="element-149">
          <div data-id="element-150">
            <div className="flex items-center space-x-3 mb-3" data-id="element-151">
              <span className="text-xs font-mono text-[#00D4FF] bg-[#00D4FF]/10 border border-[#00D4FF]/30 px-2 py-1 rounded glow-cyan" data-id="element-152">
                {activity.code}
              </span>
              <StatusBadge status={activity.status} data-id="element-153" />
              <PriorityBadge priority={activity.priority} data-id="element-154" />
            </div>
            <h1 className="text-xl font-bold text-[#E2E8F0] leading-tight" data-id="element-155">
              {activity.title}
            </h1>
          </div>

          <div className="flex flex-wrap gap-2 shrink-0" data-id="element-156">
            <button onClick={() => alert('Fitur input progres akan tersedia pada versi berikutnya.')} className="flex items-center px-3 py-2 bg-[#00D4FF] text-[#0B0F1A] rounded hover:bg-[#00D4FF]/90 transition-colors text-xs font-bold uppercase glow-cyan" data-id="element-157">
              <PlusIcon className="w-4 h-4 mr-1.5" data-id="element-158" /> PROGRES
            </button>
            <button onClick={() => alert('Fitur upload dokumen akan tersedia pada versi berikutnya.')} className="flex items-center px-3 py-2 border border-[#1E3A5F] text-[#E2E8F0] bg-[#1A2332] rounded hover:bg-[#1E3A5F] transition-colors text-xs font-bold uppercase" data-id="element-159">
              <UploadIcon className="w-4 h-4 mr-1.5 text-[#00D4FF]" data-id="element-160" /> DOKUMEN
            </button>
            <button onClick={() => onEdit(activity.id)} className="flex items-center px-3 py-2 border border-[#1E3A5F] text-[#E2E8F0] bg-[#1A2332] rounded hover:bg-[#1E3A5F] transition-colors text-xs font-bold uppercase" data-id="element-161">
              <PencilIcon className="w-4 h-4 mr-1.5 text-[#00D4FF]" data-id="element-162" /> EDIT
            </button>
            <button onClick={() => alert('Fitur cetak laporan akan tersedia pada versi berikutnya.')} className="flex items-center px-3 py-2 border border-[#1E3A5F] text-[#E2E8F0] bg-[#1A2332] rounded hover:bg-[#1E3A5F] transition-colors text-xs font-bold uppercase" data-id="element-163">
              <PrinterIcon className="w-4 h-4 mr-1.5 text-[#00D4FF]" data-id="element-164" /> CETAK
            </button>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 py-4 border-t border-[#1E3A5F] relative z-10" data-id="element-165">
          <div className="space-y-3" data-id="element-166">
            <div className="flex justify-between items-center border-b border-[#1E3A5F]/50 pb-2" data-id="element-167">
              <span className="text-xs text-[#94A3B8]" data-id="element-168">Unit Pelaksana</span>
              <span className="text-sm font-medium text-[#E2E8F0]" data-id="element-169">
                {activity.unit}
              </span>
            </div>
            <div className="flex justify-between items-center border-b border-[#1E3A5F]/50 pb-2" data-id="element-170">
              <span className="text-xs text-[#94A3B8]" data-id="element-171">Penanggung Jawab</span>
              <span className="text-sm font-medium text-[#E2E8F0]" data-id="element-172">
                {activity.personInCharge}
              </span>
            </div>
            <div className="flex justify-between items-center border-b border-[#1E3A5F]/50 pb-2" data-id="element-173">
              <span className="text-xs text-[#94A3B8]" data-id="element-174">Kategori</span>
              <span className="text-sm font-medium text-[#E2E8F0]" data-id="element-175">
                {activity.category}
              </span>
            </div>
            <div className="flex justify-between items-center border-b border-[#1E3A5F]/50 pb-2" data-id="element-176">
              <span className="text-xs text-[#94A3B8]" data-id="element-177">Klasifikasi</span>
              <span className="text-sm font-medium text-[#FF3B3B]" data-id="element-178">
                {activity.classification}
              </span>
            </div>
          </div>
          <div className="space-y-3" data-id="element-179">
            <div className="flex justify-between items-center border-b border-[#1E3A5F]/50 pb-2" data-id="element-180">
              <span className="text-xs text-[#94A3B8] flex items-center" data-id="element-181">
                <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-[#00D4FF]" data-id="element-182" />{' '}
                Tanggal Mulai
              </span>
              <span className="text-xs font-mono text-[#E2E8F0]" data-id="element-183">
                {activity.startDate}
              </span>
            </div>
            <div className="flex justify-between items-center border-b border-[#1E3A5F]/50 pb-2" data-id="element-184">
              <span className="text-xs text-[#94A3B8] flex items-center" data-id="element-185">
                <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-[#00D4FF]" data-id="element-186" />{' '}
                Target Selesai
              </span>
              <span className="text-xs font-mono text-[#E2E8F0]" data-id="element-187">
                {activity.dueDate}
              </span>
            </div>
            <div className="flex justify-between items-center border-b border-[#1E3A5F]/50 pb-2" data-id="element-188">
              <span className="text-xs text-[#94A3B8] flex items-center" data-id="element-189">
                <MapPinIcon className="w-3.5 h-3.5 mr-1.5 text-[#00D4FF]" data-id="element-190" />{' '}
                Lokasi
              </span>
              <span className="text-sm font-medium text-[#E2E8F0]" data-id="element-191">
                {activity.location}
              </span>
            </div>
            <div className="flex justify-between items-center border-b border-[#1E3A5F]/50 pb-2" data-id="element-192">
              <span className="text-xs text-[#94A3B8] flex items-center" data-id="element-193">
                <FileTextIcon className="w-3.5 h-3.5 mr-1.5 text-[#00D4FF]" data-id="element-194" />{' '}
                Dasar Perintah
              </span>
              <span className="text-sm font-medium text-[#E2E8F0]" data-id="element-195">
                {activity.instructionReference}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-196">
        {/* Left Column - Details & Progress */}
        <div className="lg:col-span-2 space-y-6" data-id="element-197">
          {/* Descriptions */}
          <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] p-6" data-id="element-198">
            <h3 className="text-sm font-semibold text-[#00D4FF] mb-3 uppercase tracking-wide flex items-center" data-id="element-199">
              <span className="w-1 h-3 bg-[#00D4FF] mr-2" data-id="element-200"></span>
              DESKRIPSI KEGIATAN
            </h3>
            <p className="text-sm text-[#E2E8F0] leading-relaxed mb-6 bg-[#0B0F1A] p-4 rounded border border-[#1E3A5F]" data-id="element-201">
              {activity.description}
            </p>

            <h3 className="text-sm font-semibold text-[#00D4FF] mb-3 uppercase tracking-wide flex items-center" data-id="element-202">
              <span className="w-1 h-3 bg-[#00D4FF] mr-2" data-id="element-203"></span>
              OUTPUT YANG DIHARAPKAN
            </h3>
            <div className="bg-[#0B0F1A] p-4 rounded border border-[#1E3A5F]" data-id="element-204">
              <p className="text-sm text-[#E2E8F0]" data-id="element-205">
                {activity.expectedOutput}
              </p>
            </div>
          </div>

          {/* Overall Progress */}
          <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] p-6" data-id="element-206">
            <div className="flex justify-between items-end mb-3" data-id="element-207">
              <h3 className="text-sm font-semibold text-[#E2E8F0] uppercase tracking-wide" data-id="element-208">
                STATUS PROGRES KESELURUHAN
              </h3>
              <span className="text-2xl font-mono font-bold text-[#00D4FF] glow-cyan" data-id="element-209">
                {activity.progressPercent}%
              </span>
            </div>
            <div className="w-full bg-[#0B0F1A] rounded-full h-4 border border-[#1E3A5F] p-0.5" data-id="element-210">
              <div className={`h-full rounded-full transition-all duration-500 relative overflow-hidden ${activity.progressPercent === 100 ? 'bg-[#00FF88] glow-green' : 'bg-[#00D4FF] glow-cyan'}`} style={{
              width: `${activity.progressPercent}%`
            }} data-id="element-211">
                <div className="absolute inset-0 scanline opacity-50" data-id="element-212"></div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] p-6" data-id="element-213">
            <h3 className="text-sm font-semibold text-[#E2E8F0] mb-6 uppercase tracking-wide flex items-center" data-id="element-214">
              <span className="w-1 h-4 bg-[#00D4FF] mr-2" data-id="element-215"></span>
              TIMELINE PROGRES
            </h3>

            {activity.progressHistory && activity.progressHistory.length > 0 ? <div className="relative border-l-2 border-[#1E3A5F] ml-3 space-y-8" data-id="element-216">
                {activity.progressHistory.map((prog, idx) => <div key={prog.id} className="relative pl-6" data-id="element-217">
                    {/* Timeline Dot */}
                    <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#0B0F1A] border-2 border-[#00D4FF] glow-cyan" data-id="element-218"></div>

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2" data-id="element-219">
                      <div data-id="element-220">
                        <span className="inline-block px-2 py-1 bg-[#00D4FF]/10 border border-[#00D4FF]/30 text-[#00D4FF] text-xs font-mono font-bold rounded mb-2 glow-cyan" data-id="element-221">
                          PROGRES: {prog.progressPercent}%
                        </span>
                        <div className="text-xs text-[#64748B] font-mono flex items-center mt-1" data-id="element-222">
                          <ClockIcon className="w-3 h-3 mr-1 text-[#00D4FF]" data-id="element-223" />
                          {new Date(prog.reportedAt).toLocaleString('id-ID')}
                        </div>
                      </div>
                      <div className="mt-2 sm:mt-0" data-id="element-224">
                        <span className={`text-xs font-bold px-2 py-1 rounded border uppercase tracking-wider ${prog.verificationStatus === 'Disetujui' ? 'bg-[#00FF88]/10 text-[#00FF88] border-[#00FF88]/30 glow-green' : prog.verificationStatus === 'Belum Diverifikasi' ? 'bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30 glow-amber' : 'bg-[#FF3B3B]/10 text-[#FF3B3B] border-[#FF3B3B]/30 glow-red'}`} data-id="element-225">
                          {prog.verificationStatus}
                        </span>
                      </div>
                    </div>

                    <div className="bg-[#0B0F1A] p-4 rounded border border-[#1E3A5F] mt-3 space-y-4" data-id="element-226">
                      <div data-id="element-227">
                        <p className="text-xs font-semibold text-[#94A3B8] uppercase mb-1.5" data-id="element-228">
                          URAIAN PERKEMBANGAN
                        </p>
                        <p className="text-sm text-[#E2E8F0]" data-id="element-229">
                          {prog.progressNote}
                        </p>
                      </div>

                      {prog.obstacleNote && prog.obstacleNote !== 'Nihil.' && <div data-id="element-230">
                          <p className="text-xs font-semibold text-[#FF3B3B] uppercase mb-1.5 flex items-center" data-id="element-231">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B3B] mr-1.5" data-id="element-232"></span>
                            KENDALA
                          </p>
                          <p className="text-sm text-[#E2E8F0]" data-id="element-233">
                            {prog.obstacleNote}
                          </p>
                        </div>}

                      {prog.nextAction && <div data-id="element-234">
                          <p className="text-xs font-semibold text-[#0EA5E9] uppercase mb-1.5 flex items-center" data-id="element-235">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#0EA5E9] mr-1.5" data-id="element-236"></span>
                            TINDAK LANJUT
                          </p>
                          <p className="text-sm text-[#E2E8F0]" data-id="element-237">
                            {prog.nextAction}
                          </p>
                        </div>}

                      <div className="pt-3 mt-3 border-t border-[#1E3A5F] flex items-center justify-between" data-id="element-238">
                        <span className="text-xs text-[#94A3B8]" data-id="element-239">
                          Dilaporkan oleh:{' '}
                          <span className="font-medium text-[#00D4FF]" data-id="element-240">
                            {prog.reportedBy}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8 text-[#94A3B8] border border-dashed border-[#1E3A5F] rounded bg-[#0B0F1A]" data-id="element-241">
                <p className="text-sm" data-id="element-242">Belum ada progres yang dilaporkan.</p>
              </div>}
          </div>
        </div>

        {/* Right Column - Members & Documents */}
        <div className="space-y-6" data-id="element-243">
          {/* Team Members */}
          <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] p-6" data-id="element-244">
            <div className="flex items-center justify-between mb-4" data-id="element-245">
              <h3 className="text-sm font-semibold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-246">
                <UsersIcon className="w-4 h-4 mr-2 text-[#00D4FF]" data-id="element-247" />
                ANGGOTA TIM
              </h3>
              <span className="bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/30 text-xs font-mono font-bold px-2 py-0.5 rounded glow-cyan" data-id="element-248">
                {activity.members.length}
              </span>
            </div>

            {activity.members.length > 0 ? <div className="space-y-4" data-id="element-249">
                {activity.members.map(member => <div key={member.id} className="flex items-center space-x-3 p-2 rounded hover:bg-[#1A2332] transition-colors border border-transparent hover:border-[#1E3A5F]" data-id="element-250">
                    <div className="w-10 h-10 rounded bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/30 flex items-center justify-center font-bold text-sm" data-id="element-251">
                      {member.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                    </div>
                    <div data-id="element-252">
                      <p className="text-sm font-semibold text-[#E2E8F0]" data-id="element-253">
                        {member.name}
                      </p>
                      <p className="text-xs text-[#00D4FF]" data-id="element-254">{member.role}</p>
                    </div>
                  </div>)}
              </div> : <p className="text-sm text-[#94A3B8]" data-id="element-255">
                Belum ada anggota tim yang ditugaskan.
              </p>}
          </div>

          {/* Documents */}
          <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] p-6" data-id="element-256">
            <div className="flex items-center justify-between mb-4" data-id="element-257">
              <h3 className="text-sm font-semibold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-258">
                <FileTextIcon className="w-4 h-4 mr-2 text-[#00D4FF]" data-id="element-259" />
                DOKUMEN TERKAIT
              </h3>
            </div>

            {activity.documents && activity.documents.length > 0 ? <div className="space-y-3" data-id="element-260">
                {activity.documents.map(doc => <div key={doc.id} className="flex items-start justify-between p-3 border border-[#1E3A5F] bg-[#0B0F1A] rounded hover:border-[#00D4FF]/50 transition-colors group" data-id="element-261">
                    <div className="flex items-start space-x-3 overflow-hidden" data-id="element-262">
                      <div className="mt-0.5 text-[#FF3B3B]" data-id="element-263">
                        <FileTextIcon className="w-4 h-4" data-id="element-264" />
                      </div>
                      <div className="min-w-0" data-id="element-265">
                        <p className="text-sm font-medium text-[#E2E8F0] truncate" title={doc.fileName} data-id="element-266">
                          {doc.fileName}
                        </p>
                        <div className="flex items-center text-xs text-[#94A3B8] mt-1 space-x-2" data-id="element-267">
                          <span data-id="element-268">{doc.fileSize}</span>
                          <span className="text-[#1E3A5F]" data-id="element-269">•</span>
                          <span className="text-[#0EA5E9]" data-id="element-270">{doc.category}</span>
                        </div>
                      </div>
                    </div>
                    <button className="text-[#00D4FF]/60 hover:text-[#00D4FF] p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-271">
                      <DownloadIcon className="w-4 h-4" data-id="element-272" />
                    </button>
                  </div>)}
              </div> : <p className="text-sm text-[#94A3B8]" data-id="element-273">
                Belum ada dokumen yang diunggah.
              </p>}
          </div>
        </div>
      </div>
    </div>;
}